/**
 * AnnotateRoofDiagram · iter303ak-fix2 — Quote Sent · Task 1 · Action 4
 * ────────────────────────────────────────────────────────────────────
 * Runner wrapper around the EXISTING SlotAnnotatorModal component.
 * Presents a "Open Roof Pin Annotator" button + a live pin-count preview.
 * When the operator clicks, the full-screen annotator modal opens with
 * the existing Point / Straight / Polygon / Softedge toolbar. Pins
 * autosave to appraisal.roof_diagram_annotations while the modal is
 * open. On close, the count refreshes and the Runner Continue button
 * unlocks (once at least 1 pin exists — or operator can skip via
 * "Continue without pins" for edge cases).
 *
 * Zero new UI — this is 100% orchestration of the existing modal.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Map, CheckCircle2, AlertCircle, ExternalLink } from 'lucide-react';
import SlotAnnotatorModal from '@/components/SlotAnnotatorModal';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export default function AnnotateRoofDiagram({ action, capturedData, onNext, onBack, onCancel, canBack }) {
  const params = action.params || {};
  const appraisalId = capturedData?.appraisal_id;
  const slot = params.slot || 'roof_plan';

  const [appraisal, setAppraisal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!appraisalId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/appraisals/${appraisalId}`);
      let app = data;
      // iter303ak-fix3 · Defensive auto-sync — if the appraisal has no
      // roof_plan_template_id (or roof_template_id) but the linked
      // property has one, back-fill it so the annotator canvas renders.
      // Fixes the edge case where Action 1's template pick landed on the
      // property but never propagated to the appraisal (older appraisals
      // pre-inheritance).
      const appTid = app?.roof_plan_template_id || app?.roof_design_template_id || app?.roof_template_id;
      if (!appTid && app?.property_id) {
        try {
          const { data: prop } = await api.get(`/properties/${app.property_id}`);
          if (prop?.roof_template_id) {
            await api.patch(`/appraisals/${appraisalId}`, {
              roof_plan_template_id: prop.roof_template_id,
            });
            app = { ...app, roof_plan_template_id: prop.roof_template_id };
          }
        } catch { /* non-fatal */ }
      }
      setAppraisal(app);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [appraisalId]);

  useEffect(() => { load(); }, [load]);

  // Persist pins back to the appraisal. Called on every pin add/edit/drag
  // via the modal — matches the existing AppraisalDetailPage pattern.
  const handlePinsChange = async (mergedPins) => {
    if (!appraisalId) return;
    setSaving(true);
    try {
      // iter303ak-fix3 · Endpoint is PATCH not PUT — matches the backend
      // route at appraisals.py line 1298. Was silently failing with 405.
      await api.patch(`/appraisals/${appraisalId}/roof-diagram-annotations`, { annotations: mergedPins });
      setAppraisal(a => a ? { ...a, roof_diagram_annotations: mergedPins } : a);
    } catch (e) {
      // Non-fatal — modal will retry on next change.
    } finally { setSaving(false); }
  };

  const pins = appraisal?.roof_diagram_annotations || [];
  const pinsInSlot = pins.filter(p => (p.slot || 'roof_plan') === slot);
  // iter303ak-fix3 · Look at all 3 template-id fields on the appraisal —
  // roof_plan_template_id (slot system, preferred), roof_design_template_id
  // (legacy), roof_template_id (fallback). Load() also back-fills from
  // property.roof_template_id if none are set.
  const templateId = appraisal?.roof_plan_template_id
    || appraisal?.roof_design_template_id
    || appraisal?.roof_template_id
    || '';
  const hasTemplate = !!templateId;
  const canContinue = pinsInSlot.length > 0;

  // Count pins by type for the summary strip.
  const countsByType = pinsInSlot.reduce((acc, p) => {
    acc[p.type] = (acc[p.type] || 0) + 1;
    return acc;
  }, {});
  const typeSummary = Object.entries(countsByType).map(([k, v]) => `${v} ${k}`).join(' · ');

  if (!appraisalId) {
    return (
      <div className="p-6 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700">
        No appraisal linked to this run — cannot annotate the roof diagram.
        {canBack && (<button onClick={onBack} className="ml-3 text-rose-800 underline">← Back</button>)}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="wf-annotate-roof-diagram">
      <div className="rounded-xl border border-purple-200 bg-purple-50/60 p-4">
        <div className="flex items-start gap-3">
          <Map className="w-5 h-5 text-purple-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-purple-900">Annotate Roof Diagram (Pin Data)</div>
            <div className="text-sm text-purple-800 mt-1">
              {params.prompt || 'Drop pins on the roof plan for each Ridge / Valley / Hip / Barge / Flashing / Face / Length / Spouting / Pipe / Flue you saw on site. Uses the existing Point / Straight / Polygon / Softedge toolbar.'}
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-4 text-sm text-slate-400 italic">Loading appraisal…</div>
      ) : !hasTemplate ? (
        <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-4">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          <div>
            <div className="font-medium">No roof template linked yet.</div>
            <div className="text-xs mt-1">{params.no_template_hint || 'Go back to Action 1 (Roof Snapshot + Template Confirm) and pick a template — the annotator needs one to render the canvas.'}</div>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-800 mb-1">
                {pinsInSlot.length} pin{pinsInSlot.length === 1 ? '' : 's'} on the roof plan
              </div>
              {typeSummary ? (
                <div className="text-xs text-slate-600" data-testid="wf-pin-count-summary">{typeSummary}</div>
              ) : (
                <div className="text-xs text-purple-700 italic flex items-center gap-1" data-testid="wf-pin-empty-state">
                  <span aria-hidden>→</span> Open the annotator to start pinning defects.
                </div>
              )}
              {saving && <div className="text-xs text-slate-400 italic mt-1">Saving pins…</div>}
            </div>
            <button
              onClick={() => setModalOpen(true)}
              data-testid="wf-open-annotator-btn"
              className="px-4 py-2 bg-purple-600 text-white text-sm font-semibold rounded hover:bg-purple-700 inline-flex items-center gap-2"
            >
              <ExternalLink size={14} /> Open Pin Annotator
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 pt-2">
        {canBack && (
          <button onClick={onBack} data-testid="wf-annotate-back"
            className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>
        )}
        {onCancel && (
          <button onClick={onCancel} data-testid="wf-annotate-cancel"
            className="px-4 py-2 text-rose-500 hover:text-rose-700 text-sm">Cancel</button>
        )}
        <span className="flex-1" />
        {!canContinue && (
          <button
            onClick={onNext}
            data-testid="wf-annotate-skip"
            className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700 hover:underline mr-2">
            Skip (no pins for this appraisal)
          </button>
        )}
        <button
          onClick={onNext}
          disabled={!canContinue}
          data-testid="wf-annotate-next"
          className="px-6 py-2 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700 disabled:opacity-40 inline-flex items-center gap-2"
        >
          <CheckCircle2 size={14} /> Continue → saved: {pinsInSlot.length} pin{pinsInSlot.length === 1 ? '' : 's'}
        </button>
      </div>

      {/* Existing modal — mount when open; unmounts on close to reset state. */}
      {modalOpen && (
        <SlotAnnotatorModal
          open={modalOpen}
          slotKey={`${slot}_template_id`}
          slotLabel="Roof Plan — Quote Sent"
          slotColour="#6366f1"
          appraisalId={appraisalId}
          templateId={templateId}
          pins={pins}
          onPinsChange={handlePinsChange}
          onClose={() => { setModalOpen(false); load(); }}
        />
      )}
    </div>
  );
}
